import { getSessionData, updateSessionData } from '../utils/cookies';
import { analytics } from '../utils/analytics';

interface JourneyStep {
    step: string;
    timestamp: Date;
    data?: Record<string, any>;
}

interface UserJourney {
    journeyId: string;
    steps: JourneyStep[];
    utmSource?: string;
    utmMedium?: string;
    utmCampaign?: string;
    utmContent?: string;
    utmTerm?: string;
    referrer?: string;
    landingPage?: string;
    startTime: Date;
    lastActive: Date;
    completed?: boolean;
    completedAt?: Date;
}

class UserJourneyService {
    private currentJourney: UserJourney | null = null;

    /**
     * Initialize a user journey
     */
    initializeJourney(): string {
        const sessionData = getSessionData();

        if (!sessionData) {
            console.error('Session data is required to initialize a user journey');
            return '';
        }

        const journeyId = `journey_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

        this.currentJourney = {
            journeyId,
            steps: [],
            utmSource: sessionData.utmData?.source,
            utmMedium: sessionData.utmData?.medium,
            utmCampaign: sessionData.utmData?.campaign,
            utmContent: sessionData.utmData?.content,
            utmTerm: sessionData.utmData?.term,
            referrer: document.referrer || undefined,
            landingPage: window.location.href,
            startTime: new Date(),
            lastActive: new Date()
        };

        // Store journey in session
        updateSessionData({
            userJourney: this.currentJourney
        });

        // Track journey start
        analytics.track('user_journey_started', 'user_journey', {
            journeyId,
            utmSource: this.currentJourney.utmSource,
            utmMedium: this.currentJourney.utmMedium,
            utmCampaign: this.currentJourney.utmCampaign,
            referrer: this.currentJourney.referrer,
            landingPage: this.currentJourney.landingPage
        });

        return journeyId;
    }    /**
     * Get the current user journey
     */
    getCurrentJourney(): UserJourney | null {
        if (this.currentJourney) {
            return this.currentJourney;
        }

        const sessionData = getSessionData();

        if (sessionData?.userJourney) {
            // Deserialize date strings back to Date objects
            const journey = sessionData.userJourney;
            this.currentJourney = {
                ...journey,
                startTime: new Date(journey.startTime),
                lastActive: new Date(journey.lastActive),
                completedAt: journey.completedAt ? new Date(journey.completedAt) : undefined,
                steps: journey.steps.map((step: any) => ({
                    ...step,
                    timestamp: new Date(step.timestamp)
                }))
            };
            return this.currentJourney;
        }

        return null;
    }

    /**
     * Record a step in the user journey
     */
    recordStep(step: string, data?: Record<string, any>): void {
        const journey = this.getCurrentJourney();

        if (!journey) {
            console.error('No active user journey found');
            return;
        }

        const journeyStep: JourneyStep = {
            step,
            timestamp: new Date(),
            data
        };

        journey.steps.push(journeyStep);
        journey.lastActive = new Date();

        // Update journey in session
        updateSessionData({
            userJourney: journey
        });

        // Track step
        analytics.track('user_journey_step', 'user_journey', {
            journeyId: journey.journeyId,
            step,
            stepNumber: journey.steps.length,
            ...data
        });
    }    /**
     * Complete the user journey
     */
    completeJourney(data?: Record<string, any>): void {
        const journey = this.getCurrentJourney();

        if (!journey) {
            console.error('No active user journey found');
            return;
        }

        journey.completed = true;
        journey.completedAt = new Date();

        // Update journey in session
        updateSessionData({
            userJourney: journey
        });

        // Calculate duration safely
        let duration = 0;
        try {
            const startTime = journey.startTime instanceof Date ? journey.startTime : new Date(journey.startTime);
            duration = journey.completedAt.getTime() - startTime.getTime();
        } catch (error) {
            console.warn('Could not calculate journey duration:', error);
        }

        // Track journey completion
        analytics.track('user_journey_completed', 'user_journey', {
            journeyId: journey.journeyId,
            steps: journey.steps.length,
            duration,
            utmSource: journey.utmSource,
            utmMedium: journey.utmMedium,
            utmCampaign: journey.utmCampaign,
            ...data
        });
    }/**
     * Abandon the user journey
     */
    abandonJourney(reason?: string): void {
        const journey = this.getCurrentJourney();

        if (!journey) {
            console.error('No active user journey found');
            return;
        }

        // Calculate duration safely
        let duration = 0;
        try {
            const startTime = journey.startTime instanceof Date ? journey.startTime : new Date(journey.startTime);
            duration = new Date().getTime() - startTime.getTime();
        } catch (error) {
            console.warn('Could not calculate journey duration:', error);
        }

        // Track journey abandonment
        analytics.track('user_journey_abandoned', 'user_journey', {
            journeyId: journey.journeyId,
            steps: journey.steps.length,
            lastStep: journey.steps.length > 0 ? journey.steps[journey.steps.length - 1].step : 'unknown',
            duration,
            reason
        });

        // Clear journey from session
        this.currentJourney = null;

        const sessionData = getSessionData();
        if (sessionData) {
            delete sessionData.userJourney;
            updateSessionData(sessionData);
        }
    }

    /**
     * Get UTM parameters from the current journey
     */
    getUtmParameters(): Record<string, string | undefined> {
        const journey = this.getCurrentJourney();

        if (!journey) {
            return {};
        }

        return {
            utm_source: journey.utmSource,
            utm_medium: journey.utmMedium,
            utm_campaign: journey.utmCampaign,
            utm_content: journey.utmContent,
            utm_term: journey.utmTerm
        };
    }    /**
     * Get journey duration in milliseconds
     */
    getJourneyDuration(): number {
        const journey = this.getCurrentJourney();

        if (!journey) {
            return 0;
        }

        try {
            const endTime = journey.completedAt || new Date();
            const startTime = journey.startTime instanceof Date ? journey.startTime : new Date(journey.startTime);
            const finalEndTime = endTime instanceof Date ? endTime : new Date(endTime);
            
            const duration = finalEndTime.getTime() - startTime.getTime();
            
            // Return 0 if duration is NaN (from invalid dates)
            return isNaN(duration) ? 0 : duration;
        } catch (error) {
            console.warn('Could not calculate journey duration:', error);
            return 0;
        }
    }

    /**
     * Get journey steps count
     */
    getStepsCount(): number {
        const journey = this.getCurrentJourney();

        if (!journey) {
            return 0;
        }

        return journey.steps.length;
    }
}

// Export singleton instance
export const userJourneyService = new UserJourneyService();